import { Component, Input } from '@angular/core';
import { RxComponent } from 'app/core/components/common/rx.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { StringUtils } from 'app/core/components/common/string-utils';
import { OrganizationDetailsService } from 'app/organization/components/organization-details/organization-details.service';
import { Router } from '@angular/router';
import { NotificationService } from 'app/core/services/notification.service';
import { SmartModalService, ModalSize } from 'app/core/services/smart-modal.service';
import { PreviewCertificateComponent, PreviewCertificateModel } from 'app/shared/components/preview-certificate/preview-certificate.component';
import { ClassesService } from 'app/solar/common/services/classes.service';

@Component({
    selector: 'itpx-update-certificate-signatories',
    templateUrl: './update-certificate-signatories.component.html',
    styleUrls: ['./update-certificate-signatories.component.css']
})
export class UpdateCertificateSignatoriesComponent extends RxComponent {

    private readonly SignatoryType = 'person-type';
    private readonly PrimarySignatoryKey = 'primary-signatory';
    private readonly SecondarySignatoryKey = 'secondary-signatory';

    @Input()
    private account: any;
    @Input()
    private cancelRoute: string;
    private form: FormGroup;

    private previewConfig: PreviewCertificateModel;
    private eLearningCertificatePreviewConfig: PreviewCertificateModel;
    private formSubmitted = new Subject();
    private previewClicked = new Subject();
    private previewCompletionCertificateClicked = new Subject();

    constructor(
        private fb: FormBuilder,
        private service: OrganizationDetailsService,
        private router: Router,
        private notificationService: NotificationService,
        private modalService: SmartModalService,
        private classesService: ClassesService
    ) {
        super();
    }

    ngOnInit() {

        let signatories = this.account.certificateSignatories;
        let primarySignatory: any = {}, secondarySignatory: any = {};
        if (signatories) {
            primarySignatory = signatories.find(s => s.type.configKey === this.PrimarySignatoryKey) || {};
            secondarySignatory = signatories.find(s => s.type.configKey === this.SecondarySignatoryKey) || {};
        }

        this.form = this.fb.group({
            primary: this.fb.group({
                name: [primarySignatory.name, Validators.required],
                designation: [primarySignatory.designation, Validators.required],
                type: this.fb.group({configKey: [this.PrimarySignatoryKey]})
            }),
            secondary: this.fb.group({
                name: [secondarySignatory.name],
                designation: [secondarySignatory.designation],
                type: this.fb.group({configKey: [this.SecondarySignatoryKey]})
            }, { validator: this.validateSecondarySignatory }),
            enableCertificates: [this.account.certificateDeliveryEnabled],
            enableCompletionCertificates: [this.account.elearningCompletionCertificateDeliveryEnabled]
        });

        this.previewConfig = {
            previewFn: (data) => this.classesService.previewCertificate(data),
            downloadFn: (data) => this.classesService.downloadCertificate(data),
            certificateTemplate: 'CLASS_COMPLETION_CERTIFICATE'
        };

        this.eLearningCertificatePreviewConfig = {
            previewFn: (data) => this.classesService.previewCertificate(data), //Change
            downloadFn: (data) => this.classesService.downloadCertificate(data), //Change,
            certificateTemplate: 'ELEARNING_COMPLETION_CERTIFICATE'
        };

        this.formSubmitted
            .map(form => this.mapToDto(form))
            .flatMap(data => this.service.updateSignatories(data).neverError())
            .bindToLifecycle(this)
            .subscribe(_ => {
                this.notificationService.success('Certificates configuration updated.');
                this.navigateToDetails();
            });

        this.previewClicked
            .map(_ => this.getCurrentSignatories())
            .map(signatories => Object.assign({}, this.previewConfig, { certData: signatories }))
            .flatMap(previewConfig => this.modalService.open(PreviewCertificateComponent, previewConfig, 'Participate Certificate Preview', ModalSize.Lg).neverError())
            .bindToLifecycle(this)
            .subscribe();

        this.previewCompletionCertificateClicked
            .map(_ => this.getCurrentSignatories())
            .map(signatories => Object.assign({}, this.eLearningCertificatePreviewConfig, { certData: signatories }))
            .flatMap(previewConfig => this.modalService.open(PreviewCertificateComponent, previewConfig, 'eLearning Completion Certificate Preview', ModalSize.Lg).neverError())
            .bindToLifecycle(this)
            .subscribe();
    }

    private getCurrentSignatories(): { signatories: Array<{ name: string, designation: string }> } {
        let form = this.form.value;
        let signatories = [{...form.primary, type: {configKey: this.PrimarySignatoryKey}}];
        if (this.hasSecondarySignatory(form)) {
            signatories.push({...form.secondary, type: {configKey: this.SecondarySignatoryKey}});
        }

        return { signatories };
    }

    private mapToDto(form) {
        let dto = {
            id: this.account.id,
            certificateSignatories: [form.primary],
            certificateDeliveryEnabled: form.enableCertificates,
            elearningCompletionCertificateDeliveryEnabled: form.enableCompletionCertificates
        };

        if (this.hasSecondarySignatory(form)) {
            dto.certificateSignatories.push(form.secondary);
        }

        return dto;
    }

    private hasSecondarySignatory(form: any): boolean {
        return form.secondary && StringUtils.notNullOrEmpty(form.secondary.name) && StringUtils.notNullOrEmpty(form.secondary.designation);
    }

    private validateSecondarySignatory(group: FormGroup) {
        let secondaryName = group.get('name').value;
        let secondaryDesignation = group.get('designation').value;

        let isInvalid = StringUtils.notNullOrEmpty(secondaryName) !== StringUtils.notNullOrEmpty(secondaryDesignation);
        group.get('name').setErrors(isInvalid ? {} : null);

        return null;
    }

    private onSubmit({ value, valid }: { value: any, valid: boolean }) {
        this.formSubmitted.next(value);
    }

    private navigateToDetails() {
        this.router.navigate([this.cancelRoute], { queryParams: { tab: 'certificates' } });
    }

    private onPreviewClick() {
        this.previewClicked.next();
    }

    private onCompletionCertificatePreviewClick(){
        this.previewCompletionCertificateClicked.next();
    }
}



// WEBPACK FOOTER //
// ./src/app/organization/components/organization-details/organization-details-edit/update-certificate-signatories/update-certificate-signatories.component.ts